import type { UnenrollmentOutcome } from "@/helpers/unenrollmentOutcome";

import { SettingsBanner } from "./SettingsBanner";

type UnenrollmentOutcomeBannerProps = {
  outcome: UnenrollmentOutcome;
  onDismiss: () => void;
};

const UNKNOWN_ACTOR = "Another Owner";

/**
 * Copy per outcome, from the unenrollment design thread. Owners only: the
 * backend serves the pending request to Owners alone, so no other role can
 * observe a pause ending (see `useCoSigningPause`). The design's Financial
 * Controller copy returns once the backend opens that read. The `ended` kind
 * is the fallback for a pause that vanished before the backend could say who
 * cancelled or rejected it.
 */
const bannerCopy = (outcome: UnenrollmentOutcome): { title: string; body: string } => {
  const actor = outcome.actor?.name?.trim() || UNKNOWN_ACTOR;
  const who = outcome.byMe ? "You" : actor;
  const staysOn = "so co-signing stays on and the pause has ended.";
  switch (outcome.kind) {
    case "cancelled":
      return {
        title: "Request to turn off co-signing canceled",
        body: `${who} canceled the request, ${staysOn}`,
      };
    case "rejected":
      return {
        title: "Request to turn off co-signing rejected",
        body: `${who} rejected the request, ${staysOn}`,
      };
    case "expired":
      return {
        title: "Request to turn off co-signing expired",
        body: `Two Owners didn't sign within 4 days, ${staysOn}`,
      };
    case "ended":
      return {
        title: "Request to turn off co-signing ended",
        body: `The request was canceled or rejected, ${staysOn}`,
      };
  }
};

export const UnenrollmentOutcomeBanner = ({
  outcome,
  onDismiss,
}: UnenrollmentOutcomeBannerProps) => {
  const { title, body } = bannerCopy(outcome);
  return (
    <SettingsBanner
      variant="warning"
      title={title}
      onDismiss={onDismiss}
      className="UnenrollmentOutcomeBanner"
    >
      {body}
    </SettingsBanner>
  );
};
